import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Input, Button } from '../../../components/ui';
import AIPricingModal from '../../../components/forms/AIPricingModal';

/**
 * Step 3: Pricing
 *
 * Ticket price and quantity with optional AI pricing suggestion.
 */
export default function StepPricing({ onNext, onBack }) {
  const [showAIModal, setShowAIModal] = useState(false);
  const {
    register,
    watch,
    setValue,
    formState: { errors },
    trigger,
  } = useFormContext();

  const ticketPrice = watch('ticketPrice');
  const ticketQuantity = watch('ticketQuantity');

  // Item details for AI pricing
  const photos = watch('photos');
  const title = watch('title');
  const description = watch('description');
  const category = watch('category');
  const condition = watch('condition');

  // Check if we have enough info for AI suggestion
  const canGetAISuggestion = title?.length >= 5 && description?.length >= 20 && category && condition;

  const priceInEuros = ticketPrice ? ticketPrice / 100 : '';
  const totalRevenue = ticketPrice && ticketQuantity ? (ticketPrice * ticketQuantity) / 100 : 0;

  const isValid = ticketPrice >= 100 && ticketPrice <= 50000 && ticketQuantity >= 10 && ticketQuantity <= 1000;

  async function handleContinue() {
    const valid = await trigger(['ticketPrice', 'ticketQuantity']);
    if (valid) {
      onNext();
    }
  }

  function handlePriceChange(e) {
    const euros = parseFloat(e.target.value) || 0;
    const cents = Math.round(euros * 100);
    setValue('ticketPrice', cents, { shouldValidate: true });
  }

  function handleApplySuggestion(suggestion) {
    setValue('ticketPrice', suggestion.ticketPrice, { shouldValidate: true });
    setValue('ticketQuantity', suggestion.ticketQuantity, { shouldValidate: true });
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-display text-cream mb-2">
              Set Your Price
            </h1>
            <p className="text-cream/60">
              Choose how much each entry costs and how many to sell.
            </p>
          </div>
          {/* AI Pricing Button */}
          {canGetAISuggestion && (
            <button
              type="button"
              onClick={() => setShowAIModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-primary/20 to-accent/20 border border-primary/30 rounded-lg text-primary hover:from-primary/30 hover:to-accent/30 transition-colors text-sm font-medium whitespace-nowrap"
            >
              <span>🤖</span>
              <span>Get AI Suggestion</span>
            </button>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6 mb-8">
        {/* Ticket Price */}
        <div>
          <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-2">
            Entry Price
          </label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-primary font-mono font-bold text-lg">€</span>
            <input
              type="number"
              step="0.01"
              min="1"
              max="500"
              value={priceInEuros}
              onChange={handlePriceChange}
              className="w-full bg-midnight border border-primary/30 rounded-lg px-4 py-4 pl-10 text-cream text-xl font-mono font-bold placeholder:text-cream/30 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
              placeholder="5.00"
            />
          </div>
          <p className="text-cream/40 text-xs mt-2">€1 - €500 per entry</p>
          {errors.ticketPrice?.message && (
            <p className="text-error text-sm mt-1">{errors.ticketPrice.message}</p>
          )}
        </div>

        {/* Ticket Quantity */}
        <div>
          <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-2">
            Number of Entries
          </label>
          <input
            type="number"
            min="10"
            max="1000"
            {...register('ticketQuantity', { valueAsNumber: true })}
            className="w-full bg-midnight border border-primary/30 rounded-lg px-4 py-4 text-cream text-xl font-mono font-bold placeholder:text-cream/30 focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none transition-all"
            placeholder="50"
          />
          <p className="text-cream/40 text-xs mt-2">10 - 1000 entries</p>
          {errors.ticketQuantity?.message && (
            <p className="text-error text-sm mt-1">{errors.ticketQuantity.message}</p>
          )}
        </div>

        {/* Quick Select Buttons */}
        <div>
          <label className="block text-xs font-medium text-cream/70 uppercase tracking-wider mb-3">
            Quick Select
          </label>
          <div className="flex flex-wrap gap-2">
            {[25, 50, 100, 200, 500].map((qty) => (
              <button
                key={qty}
                type="button"
                onClick={() => setValue('ticketQuantity', qty, { shouldValidate: true })}
                className={`px-4 py-2.5 rounded-lg border font-mono font-semibold transition-all ${
                  ticketQuantity === qty
                    ? 'border-primary bg-primary/20 text-primary shadow-sm shadow-primary/20'
                    : 'border-midnight-lighter bg-midnight text-cream/60 hover:border-primary/30 hover:text-cream'
                }`}
              >
                {qty}
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-br from-primary/10 via-midnight-light to-accent/5 border border-primary/20 rounded-xl p-6">
          <h3 className="text-xs font-medium text-cream/70 uppercase tracking-wider mb-4">Potential Earnings</h3>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-cream/60 text-sm">Entry price</span>
              <span className="text-cream font-mono font-bold">€{priceInEuros ? Number(priceInEuros).toFixed(2) : '0.00'}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-cream/60 text-sm">Total entries</span>
              <span className="text-cream font-mono font-bold">{ticketQuantity || 0}</span>
            </div>
            <div className="border-t border-cream/10 pt-4 mt-4">
              <div className="flex justify-between items-baseline">
                <span className="text-cream font-medium">Maximum Revenue</span>
                <span className="text-3xl font-mono font-bold text-primary">€{totalRevenue.toFixed(2)}</span>
              </div>
              <p className="text-cream/40 text-xs mt-2">
                {ticketQuantity && ticketPrice ? (
                  <>If all {ticketQuantity} entries sell at €{Number(priceInEuros).toFixed(2)} each</>
                ) : (
                  <>Set price and quantity above</>
                )}
              </p>
              <p className="text-cream/30 text-xs mt-1">5% platform fee on successful sales</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between gap-4">
        <Button variant="outline" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleContinue} disabled={!isValid}>
          Continue
        </Button>
      </div>

      {/* AI Pricing Modal */}
      <AIPricingModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onApply={handleApplySuggestion}
        itemDetails={{ photos, title, description, category, condition }}
      />
    </div>
  );
}
